import { useMemo, useCallback } from 'react';
import {
  useAddressFormatter,
  useTranslation,
  type TFunction,
} from '@wix/yoshi-flow-editor';
import { AdditionalInfoLabel } from '@wix/ambassador-restaurants-v1-fulfillment-method/types';
import { SchedulingType } from '@wix/ambassador-restaurants-operations-v1-operation/types';

import { formatAddressString } from '../../../../utils/address';
import { DEFAULT_TIMEZONE } from '../../../../utils/constants';
import {
  DispatchType,
  type DispatchInfo,
} from '../../../../types/businessTypes';
import { RequestStatus } from '../../../../utils/wrapRequest';
import {
  formatDispatchTime,
  getDineInLabel,
} from '../../../../utils/formatDispatchTime';
import { isDispatchTypeSelectable } from '../../../../services/fulfillmentsService/helpers';
import { useOnlineOrdersStorage } from '../../storage';

const getAddressLabel = ({
  selectedDispatchType,
  formattedAddress,
  dispatchInfo,
  t,
}: {
  selectedDispatchType: DispatchType | undefined;
  formattedAddress: string;
  dispatchInfo: DispatchInfo | undefined;
  t: TFunction;
}): string | null => {
  if (selectedDispatchType === DispatchType.DELIVERY) {
    return `${t('header_olo.address.delivery')}${formattedAddress}`;
  }

  if (selectedDispatchType === DispatchType.DINE_IN) {
    const dineInLabel = getDineInLabel({ dispatchInfo, t });
    const seating = dispatchInfo?.dineInCustomerResponse ?? '';
    const seatingLine = [dineInLabel, seating].filter(Boolean).join(' ');

    return `${t('header_olo.address.dineIn')}${
      seatingLine ? ` ${seatingLine}` : ''
    }`;
  }

  if (selectedDispatchType === DispatchType.PICKUP && formattedAddress) {
    return `${t('header_olo.address.pickup')}${formattedAddress}`;
  }

  return null;
};

export const useHooks = () => {
  const { t } = useTranslation();
  const formatAddress = useAddressFormatter();
  const {
    selectedDispatchType,
    dispatchesInfo,
    timezone,
    regionalSettings,
    operations,
    currentOperation,
    acceptingDispatchTypesByOperationId,
    fetchTimeSlotsStatus,
    fetchFulfillmentMethodsStatus,
    openFulfillmentModal,
  } = useOnlineOrdersStorage();

  const isDispatchInfoReady =
    fetchTimeSlotsStatus === RequestStatus.RESOLVED &&
    fetchFulfillmentMethodsStatus === RequestStatus.RESOLVED;

  const dispatchInfo = selectedDispatchType
    ? dispatchesInfo[selectedDispatchType]
    : undefined;

  const formattedAddress = useMemo(
    () => formatAddressString(dispatchInfo?.address, formatAddress),
    [dispatchInfo?.address, formatAddress],
  );

  const resolvedTimezone = timezone || DEFAULT_TIMEZONE;
  const locale = regionalSettings ?? '';

  const isLoading =
    !currentOperation ||
    !selectedDispatchType ||
    fetchTimeSlotsStatus === RequestStatus.LOADING ||
    fetchFulfillmentMethodsStatus === RequestStatus.LOADING;

  const isAsapOnly =
    currentOperation?.schedulingType !== SchedulingType.PREORDER &&
    !currentOperation?.allowAsapFutureHandling;

  const isCurrentDispatchAvailable = !!dispatchInfo?.selectedTimeSlot;
  const shouldShowTimeInfo = !isLoading && isCurrentDispatchAvailable;

  const isMultiLocation = operations.length > 1;
  const isDineInWithNoAdditionalInfo =
    selectedDispatchType === DispatchType.DINE_IN &&
    dispatchInfo?.dineInAdditionalLabel ===
      AdditionalInfoLabel.NO_ADDITIONAL_INFO_LABEL;

  const configuredDispatchTypes = Object.keys(dispatchesInfo) as DispatchType[];
  const areAllPickerOptionsDisabled =
    isDispatchInfoReady &&
    !isMultiLocation &&
    configuredDispatchTypes.length > 0 &&
    configuredDispatchTypes.every(
      (type) => !isDispatchTypeSelectable(type, dispatchesInfo[type]),
    );

  const timeString = useMemo(
    () =>
      selectedDispatchType
        ? formatDispatchTime({
            selectedTimeSlot: dispatchInfo?.selectedTimeSlot,
            dispatchType: selectedDispatchType,
            t,
            timezone: resolvedTimezone,
            locale,
          })
        : '',
    [
      dispatchInfo?.selectedTimeSlot,
      selectedDispatchType,
      t,
      resolvedTimezone,
      locale,
    ],
  );

  const addressLabel = getAddressLabel({
    selectedDispatchType,
    formattedAddress,
    dispatchInfo,
    t,
  });

  const hasTimeChangeButton = !isAsapOnly && shouldShowTimeInfo;

  const hasAddressChangeButton =
    !isLoading &&
    (selectedDispatchType === DispatchType.DELIVERY ||
      isMultiLocation ||
      (selectedDispatchType === DispatchType.DINE_IN &&
        !isDineInWithNoAdditionalInfo));

  const showAddressChangeButton =
    hasAddressChangeButton &&
    (addressLabel !== null || selectedDispatchType === DispatchType.DELIVERY);

  const addressChangeButtonLabel =
    selectedDispatchType === DispatchType.DELIVERY && !formattedAddress
      ? t('header_olo.address.delivery.add')
      : t('header_olo.address.delivery.change');

  const showTimeRow = timeString !== '' && !areAllPickerOptionsDisabled;
  const showAddressRow =
    (addressLabel !== null || showAddressChangeButton) &&
    !areAllPickerOptionsDisabled;

  const onChange = useCallback(() => {
    if (!currentOperation) {
      return;
    }

    openFulfillmentModal({
      operations,
      currentOperation,
      dispatchesInfo,
      selectedDispatchType,
      acceptingDispatchTypesByOperationId,
    });
  }, [
    operations,
    currentOperation,
    dispatchesInfo,
    selectedDispatchType,
    acceptingDispatchTypesByOperationId,
    openFulfillmentModal,
  ]);

  return {
    timeString,
    timeChangeButtonLabel: t('header_olo.time.change'),
    addressLabel,
    addressChangeButtonLabel,
    showTimeRow,
    showAddressRow,
    showTimeChangeButton: hasTimeChangeButton,
    showAddressChangeButton,
    onChangeTime: onChange,
    onChangeAddress: onChange,
  };
};
